import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Shield, Loader2, ShieldCheck, Users, ClipboardList } from 'lucide-react';

const QUICK_ACCOUNTS = [
  { username: 'admin', password: 'Admin@123', name: 'Admin User', role: 'Administrator', icon: ShieldCheck },
  { username: 'tl1', password: 'TeamLead@1', name: 'Ricardo Flores', role: 'Team Leader', icon: Users },
  { username: 'tl2', password: 'TeamLead@2', name: 'Carmen Aquino', role: 'Team Leader', icon: Users },
  { username: 'surveyor1', password: 'Survey@1', name: 'Angelo Bautista', role: 'Surveyor', icon: ClipboardList },
  { username: 'surveyor2', password: 'Survey@2', name: 'Liza Morales', role: 'Surveyor', icon: ClipboardList },
  { username: 'surveyor3', password: 'Survey@3', name: 'Marco Villanueva', role: 'Surveyor', icon: ClipboardList },
  { username: 'surveyor4', password: 'Survey@4', name: 'Diana Soriano', role: 'Surveyor', icon: ClipboardList },
];

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [quickPending, setQuickPending] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const routeFor = (role) => {
    if (role === 'admin') return '/admin';
    if (role === 'team_leader') return '/team-leader';
    return '/surveyor';
  };

  const doLogin = async (u, p) => {
    setError('');
    try {
      const user = await login(u, p);
      navigate(routeFor(user.role));
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Invalid credentials');
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await doLogin(username, password);
    } catch (err) {
      /* error already surfaced */
    }
    setLoading(false);
  };

  const handleQuickLogin = async (account) => {
    setUsername(account.username);
    setPassword(account.password);
    setQuickPending(account.username);
    try {
      await doLogin(account.username, account.password);
    } catch (err) {
      /* error already surfaced */
    }
    setQuickPending(null);
  };

  const busy = loading || quickPending !== null;

  const QuickButton = ({ account }) => {
    const Icon = account.icon;
    const pending = quickPending === account.username;
    return (
      <button
        type="button"
        data-testid={`quick-login-${account.username}`}
        onClick={() => handleQuickLogin(account)}
        disabled={busy}
        className="flex items-center gap-2.5 w-full text-left rounded-lg border border-slate-200 bg-white px-3 py-2.5 transition-colors hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="shrink-0 flex items-center justify-center w-8 h-8 rounded-md bg-slate-900 text-blue-400">
          {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium text-slate-900 truncate">{account.name}</span>
          <span className="block text-xs text-slate-500 truncate">{account.role} &middot; {account.username}</span>
        </span>
      </button>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden py-8">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1hcCUyMG5ldHdvcmslMjBkYXRhfGVufDB8fHx8MTc3NjA1MDY0OHww&ixlib=rb-4.1.0&q=85)`,
        }}
      />
      <div className="absolute inset-0 bg-slate-950/80" />

      <Card className="relative z-10 w-full max-w-md mx-4 my-auto border-slate-700 bg-white/95 backdrop-blur-sm shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-14 h-14 rounded-xl bg-slate-900 flex items-center justify-center mb-4">
            <Shield className="w-7 h-7 text-blue-400" />
          </div>
          <CardTitle className="font-heading text-2xl text-slate-900">
            Election Survey System
          </CardTitle>
          <CardDescription className="text-slate-500">
            Sign in to access the system
          </CardDescription>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div data-testid="login-error" className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded-lg border border-red-200">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username" className="text-slate-700">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                type="text"
                placeholder="admin"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="bg-white border-slate-300 focus:border-blue-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-white border-slate-300 focus:border-blue-500"
              />
            </div>

            <Button
              data-testid="login-submit-button"
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              disabled={busy}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs font-medium uppercase tracking-wider text-slate-400">
                  One-click sign in
                </span>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Admin</p>
                <div className="grid grid-cols-1 gap-2">
                  {QUICK_ACCOUNTS.filter(a => a.role === 'Administrator').map(a => (
                    <QuickButton key={a.username} account={a} />
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Team Leaders</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {QUICK_ACCOUNTS.filter(a => a.role === 'Team Leader').map(a => (
                    <QuickButton key={a.username} account={a} />
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Surveyors</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {QUICK_ACCOUNTS.filter(a => a.role === 'Surveyor').map(a => (
                    <QuickButton key={a.username} account={a} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
